import { Component, Inject, Prop } from 'vue-property-decorator';

import { FiltroPrograma } from '@/shared/model/filtro-programa.model';
import ApeironComponent from '@/components/commons/apeiron-component.model';
import { Programa } from '@/shared/model/programa.model';
import PersonalBecaService from '@/entities/seguimiento/personal-beca.service';
import { RolAutoridad } from '@/shared/model/enumerations/rol-autoridad.model';
import { Area } from '@/shared/model/enumerations/area';
import { IPersonalBeca } from '@/shared/model/seguimiento/personal-beca.model';
import { IUsuarioRol, UsuarioRol } from '@/shared/model/usuarioRol';
import { Solucion } from '@/shared/model/solucion.model';
import { Solicitud } from '@/shared/model/solicitud.model';
import ParticipanteService from '@/components/participantes/participante.service';
import SolicitudService from '@/entities/solicitud/solicitud.service';
import { IFiltro } from '@/shared/model/filtro.model';
import { EstadoSolicitud } from '@/shared/model/enumerations/estado-solicitud.model';
import ProgramaSelectComponent from '@/entities/snp/snippets/programa-select/programa-select.vue';
import ProgramaResumenComponent from '@/entities/snp/snippets/programa-resumen/programa-resumen.vue';
import ProgramaService from '@/shared/programa/programa.service';
import PdfsSolicitudPosgradoComponent from '@/entities/snp/snippets/pdfsSolicitudPosgrado/pdfs-solicitud-posgrado.vue';
import { TipoSolucion } from '@/shared/model/enumerations/tipo-solucion.model';

@Component({
  components: {
    'programa-select': ProgramaSelectComponent,
    'programa-resumen': ProgramaResumenComponent,
    'pdfs-solicitud-posgrado': PdfsSolicitudPosgradoComponent,
  },
})
export default class ProgramaCapturaComponent extends ApeironComponent {
  public filtro = new FiltroPrograma();
  form = { programa: new Programa() };

  public formularioRequerido = false;
  private usuarios: UsuarioRol[] = [];
  @Inject('personalBecaService') protected readonly personalBecaService: () => PersonalBecaService;
  @Inject('participanteService') protected readonly participanteService: () => ParticipanteService;
  @Inject('solicitudService') protected readonly solicitudService: () => SolicitudService;
  @Inject('programaService') protected readonly programaService: () => ProgramaService;

  @Prop({ required: false })
  public solucion: Solucion;

  @Prop({ required: true })
  public solicitud: Solicitud;

  public isDownloading = false;
  public programaSeleccionado = false;
  public programaGuardado = false;

  public actualizandoPrograma = false;

  public programaSeleccionadoValido = false;

  created() {
    this.filtro.coordinadorCvu = this.permisos.usuario.cvu;
    this.filtro.area = this.solucion?.params?.area ?? Area.POSGRADOS;

    if (this.formModel?.programa) {
      this.form.programa = { ...this.form.programa, ...JSON.parse(JSON.stringify(this.formModel.programa)) };
    } else {
      this.formularioRequerido = true;
    }

    this.programaSeleccionado = this.formModel?.programa != undefined;
    this.programaGuardado = this.formModel?.programa?.id != undefined;
  }

  /**
   * En el guardado se almacena la información del programa y el usuario asistente activo
   */
  public guardarPrograma(): void {
    this.programaGuardado = true;
    const usuarioAsistente = this.form.programa?.asistentes?.find(asistente => asistente.activo)?.usuario;
    const usuario = this.form.programa?.coordinadores?.find(coordinador => coordinador.activo)?.usuario;
    const datosEnviar = { programa: this.form.programa, usuarioAsistente: usuarioAsistente, usuario: usuario };
    this.$emit('guardarPrograma', datosEnviar);
  }

  /**
   * En el cambio de selección de programa se obtienen los usuarios con roles de COORDINADOR_INSTITUCIONAL
   * y ASISTENTE_COORDINADOR_INSTITUCIONAL y asignarlos a la solicitud
   * @param programa Programa seleccionado
   */
  public onProgramaChange(programa: Programa) {
    this.isDownloading = true;
    this.programaSeleccionadoValido = false;
    const filtroSol: IFiltro = {};
    filtroSol.solucionId = this.solucion.id;
    filtroSol.clavePrograma = programa.clave;
    filtroSol.estadosSolicitud = [EstadoSolicitud.EN_CAPTURA, EstadoSolicitud.ENVIADA, EstadoSolicitud.EN_FIRMA, EstadoSolicitud.APROBADA];
    const paginationQuery = {
      page: 0,
      size: 200,
      sort: 'asc.id',
      filter: filtroSol,
    };
    this.solicitudService()
      .contarSolicitudes(TipoSolucion.POSGRADO_NACIONAL, paginationQuery)
      .then(result => {
        if (result > 0) {
          this.alertService().showError(
            this,
            this.$t('apeironGwApp.programa.form.programaConSolicitud', 'Solicitud en proceso').toString()
          );
          this.form.programa = new Programa();
          return;
        } else {
          this.programaSeleccionadoValido = true;
        }
      })
      .finally(() => {
        this.isDownloading = false;
      });

    this.usuarios = [];
    this.asignarValoresASolicitud(programa);
    const clavesInstitucion = programa.instituciones.map(institucion => institucion.claveInstitucion);
    clavesInstitucion.forEach(claveInstitucion => {
      this.personalBecaService()
        .findByClaveInstitucionRoles(
          claveInstitucion,
          [RolAutoridad.COORDINADOR_INSTITUCIONAL, RolAutoridad.ASISTENTE_COORDINADOR_INSTITUCIONAL],
          Area.POSGRADOS
        )
        .then(result => {
          this.usuarios.push(...this.convertirPersonalBecaAUsuarioRol(result));
        });
    });
  }

  /**
   * Convierte la lista de personal beca a una lista de usuario rol
   * @param personalBeca Objeto de personal beca
   * @returns La lista de usuario a asignarse a la solicitud
   */
  private convertirPersonalBecaAUsuarioRol(personalBeca: IPersonalBeca[]): IUsuarioRol[] {
    const usuarios = [];
    personalBeca.forEach(personal => {
      const usuarioROL = new UsuarioRol();
      usuarioROL.cvu = personal.cvu;
      usuarioROL.nombreCompleto = personal.nombreCompleto;
      usuarioROL.usuario = personal.usuario;
      usuarioROL.rol = personal.rol;
      usuarios.push(usuarioROL);
    });
    return usuarios;
  }

  private asignarValoresASolicitud(programa: Programa) {
    if (programa.aprobada) {
      this.solicitud.cuota = programa.cuota;
      this.solicitud.planDeEstudios = programa.planDeEstudios;
      this.solicitud.lies = programa.lies;
      this.solicitud.pronace = programa.pronace;
      this.solicitud.programasIntegrados = programa.programasIntegrados;
      this.solicitud.areasConocimiento = programa.areasConocimiento;
      this.solicitud.opcionesTerminales = programa.opcionesTerminales;
      this.solicitud.temas = programa.temas;
    }
  }

  get integrantesClonados(): boolean {
    return (this.solicitud?.integrantesClonados ?? 0) > 0;
  }

  public clonarParticipacionesMatriculas(): void {
    this.isDownloading = true;
    this.participanteService()
      .clonarParticionacionesMatriculas(this.form.programa.clave, this.solicitud.id)
      .then(resultado => {
        this.isDownloading = false;
        this.solicitud.integrantesClonados = resultado;
        this.alertService().showSuccess(this, this.$t('apeironGwApp.programa.clonacionExitosa', 'Exitoso').toString());
      })
      .catch(() => {
        this.isDownloading = false;
        this.alertService().showError(this, this.$t('apeironGwApp.programa.clonacionFallida', 'Falla').toString());
      });
  }

  public actualizarPrograma(): void {
    this.actualizandoPrograma = true;
    this.programaService()
      .find(this.form.programa.id)
      .then(programa => {
        this.form.programa = programa;
        this.guardarPrograma();
      })
      .catch(() => {
        this.alertService().showError(this, this.$t('apeironGwApp.programa.actualizacioProgramaFalla').toString());
      })
      .finally(() => {
        this.actualizandoPrograma = false;
      });
  }

  /**
   * Indicador para permitir el modo escritura
   */
  get esEscritura(): boolean {
    return this.esEscrituraConEstadoEnCaptura(this.solicitud.estado);
  }
}
